import { validateNickname } from '../models/nickname';
import type { SubmitScorePayload } from '../models/submit-score-payload';
import type {
  ScoreSubmissionStatus,
  SubmitScoreSuccessResult,
} from '../models/submit-score-result';
import { LeaderboardRequestError, type LeaderboardRepository } from './leaderboard-service';

export type SubmissionOutcome = {
  status: ScoreSubmissionStatus;
  message: string;
  result?: SubmitScoreSuccessResult;
};

export class ScoreSubmissionService {
  private readonly submittedSessions = new Set<string>();
  private submittingSession?: string;

  public constructor(private readonly repository: LeaderboardRepository) {}

  public async submit(payload: SubmitScorePayload): Promise<SubmissionOutcome> {
    const nickname = validateNickname(payload.nickname);
    if (!nickname.valid) return { status: 'validation-error', message: nickname.message };
    if (this.submittedSessions.has(payload.sessionId)) {
      return { status: 'already-submitted', message: 'SCORE ALREADY SAVED' };
    }
    if (this.submittingSession === payload.sessionId) {
      return { status: 'submitting', message: 'TRANSMITTING SCORE...' };
    }
    this.submittingSession = payload.sessionId;
    try {
      const result = await this.repository.submitScore({ ...payload, nickname: nickname.nickname });
      if (!result.success) return mapError(result.code, result.message);
      this.submittedSessions.add(payload.sessionId);
      return { status: 'success', message: 'SCORE SAVED', result };
    } catch (error) {
      if (error instanceof LeaderboardRequestError) return mapError(error.code, error.message);
      return { status: 'network-error', message: 'NETWORK LOST — TRY AGAIN' };
    } finally {
      this.submittingSession = undefined;
    }
  }
}

const mapError = (code: string, fallback: string): SubmissionOutcome => {
  if (code === 'SESSION_ALREADY_SUBMITTED') {
    return { status: 'already-submitted', message: 'SCORE ALREADY SAVED' };
  }
  if (code === 'RATE_LIMITED')
    return { status: 'rate-limited', message: 'TOO MANY ATTEMPTS — WAIT' };
  if (
    [
      'INVALID_PAYLOAD',
      'INVALID_NICKNAME',
      'INVALID_SCORE',
      'INVALID_SESSION_ID',
      'INVALID_DURATION',
      'SCORE_MISMATCH',
      'IMPLAUSIBLE_RESULT',
      'SESSION_PAYLOAD_CONFLICT',
    ].includes(code)
  ) {
    return { status: 'validation-error', message: fallback.toUpperCase() };
  }
  if (code === 'NETWORK_ERROR')
    return { status: 'network-error', message: 'NETWORK LOST — TRY AGAIN' };
  return { status: 'server-error', message: 'SCORE SERVICE UNAVAILABLE' };
};
